import Column from "@/components/Ui/Column";
import Row from "@/components/Ui/Row";
import VendorLink from "@/components/VendorLink";
import DataInspector from "@/components/portal/DataInspector";
import ImageProxy from "@/components/portal/ImageProxy";
import InfoList from "@/components/portal/sidebar/InfoList";
import { Button } from "@/components/shadcn/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/shadcn/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { PROFILE_API } from "@/constants/env";
import { useVendorContext } from "@/contexts/vendor-context";
import useSesamyAuthFetch from "@/hooks/use-sesamy-auth-fetch";
import { ProfileUser } from "@/types/user";
import { getNameFromProfile } from "@/utils/users";
import { Minus, Plus } from "lucide-react";
import { useState, type ReactNode } from "react";
import useSWR from "swr";

type Props = {
  userId?: string;
  data?: ProfileUser;
  nonInteractive?: boolean;
  showDetails?: boolean;
};

const extractUserData = (data?: ProfileUser) => {
  const name = getNameFromProfile(data, false);
  const avatar = data?.settings?.picture || "placeholder";
  const email = data?.email;
  const { addresses, deliveryAddressId, billingAddressId } =
    data?.settings ?? {};
  const deliveryAddress =
    addresses?.find((a) => a.id === deliveryAddressId) ?? addresses?.[0];
  const deliveryAddressLine = deliveryAddress
    ? [
        deliveryAddress.street,
        deliveryAddress.zip,
        deliveryAddress.city,
        deliveryAddress.country,
      ]
        .filter(Boolean)
        .join(", ")
    : null;
  const billingAddress =
    addresses?.find((a) => a.id === billingAddressId) ?? addresses?.[0];
  const billingAddressLine =
    billingAddress && billingAddress !== deliveryAddress
      ? [
          billingAddress.street,
          billingAddress.zip,
          billingAddress.city,
          billingAddress.country,
        ]
          .filter(Boolean)
          .join(", ")
      : null;
  return { name, avatar, deliveryAddressLine, billingAddressLine, email };
};

const extractUserDetails = (data?: ProfileUser) => {
  const items: [string, ReactNode][] = [];
  const { deliveryAddressLine, billingAddressLine } = extractUserData(data);

  if (data?.userId) items.push(["User ID", data.userId]);
  if (data?.sequentialUserId)
    items.push(["Sequential ID", data.sequentialUserId]);

  items.push(["Email address", data?.email || ""]);

  if (data?.settings?.mobilePhone)
    items.push(["Mobile phone", data.settings.mobilePhone]);
  if (data?.settings?.birthday)
    items.push(["Birthday", data.settings.birthday.split("T")[0]]);
  if (deliveryAddressLine)
    items.push(["Delivery address", deliveryAddressLine]);
  if (billingAddressLine) items.push(["Billing address", billingAddressLine]);
  if (data?.settings?.businessDetails?.companyName)
    items.push(["Company", data.settings.businessDetails.companyName]);
  if (data?.settings?.businessDetails?.vatNr)
    items.push(["VAT number", data.settings.businessDetails.vatNr]);

  return items;
};

const User = ({
  userId,
  data: passedData,
  nonInteractive,
  showDetails,
}: Props) => {
  const { selectedVendor } = useVendorContext();
  const { fetcher, isReady } = useSesamyAuthFetch();
  const { data: fetchedData } = useSWR<ProfileUser>(
    isReady &&
      userId &&
      `${PROFILE_API}/user/${userId}/vendors/${selectedVendor}`,
    fetcher,
  );

  const data = fetchedData || passedData;
  const { name, avatar, deliveryAddressLine, email } = extractUserData(data);
  const details = showDetails ? extractUserDetails(data) : [];
  const hasDetails = details.length > 0;
  const [detailsOpen, setDetailsOpen] = useState(false);

  const customerHref =
    userId && !nonInteractive ? `/customers/${userId}` : undefined;
  const textLinkHoverClass = customerHref
    ? "text-current/85 transition-colors hover:text-current"
    : "";
  const editAddressLink = hasDetails ? null : (
    <VendorLink
      className={textLinkHoverClass}
      href={customerHref && `${customerHref}#CHANGE_ADDRESS`}
    >
      {deliveryAddressLine}
    </VendorLink>
  );

  const row = (
    <Row className="text-foreground w-full gap-3" left>
      <DataInspector data={data}>
        <VendorLink href={customerHref}>
          <ImageProxy
            src={avatar}
            alt={name}
            size="size-10"
            className="rounded-full"
          />
        </VendorLink>
      </DataInspector>
      <Column className="flex-1 gap-0.5 overflow-hidden" left>
        <div className="w-full truncate text-sm font-medium">
          {editAddressLink && name && (
            <>
              <VendorLink className={textLinkHoverClass} href={customerHref}>
                {name}
              </VendorLink>{" "}
              -{" "}
            </>
          )}
          <VendorLink
            className={textLinkHoverClass}
            href={
              editAddressLink
                ? `${customerHref}#CHANGE_EMAIL_ADDRESS`
                : customerHref
            }
          >
            {!editAddressLink && name ? name : email}
          </VendorLink>
        </div>
        {(editAddressLink || name) && (
          <div className="text-muted-foreground w-full truncate text-xs">
            {editAddressLink || (
              <VendorLink
                className={textLinkHoverClass}
                href={customerHref && `${customerHref}#CHANGE_EMAIL_ADDRESS`}
              >
                {email}
              </VendorLink>
            )}
          </div>
        )}
      </Column>
      {hasDetails && (
        <Tooltip>
          <TooltipTrigger asChild>
            <CollapsibleTrigger asChild>
              <Button variant="outline" size="icon">
                {detailsOpen ? (
                  <Minus className="size-4" />
                ) : (
                  <Plus className="size-4" />
                )}
              </Button>
            </CollapsibleTrigger>
          </TooltipTrigger>
          <TooltipContent>
            {detailsOpen ? "Hide details" : "Show details"}
          </TooltipContent>
        </Tooltip>
      )}
    </Row>
  );

  if (!hasDetails) return row;

  return (
    <Collapsible
      className="w-full"
      open={detailsOpen}
      onOpenChange={setDetailsOpen}
    >
      {row}
      <CollapsibleContent className="pt-4">
        <InfoList
          items={details}
          editHrefs={
            customerHref
              ? {
                  "Email address": `${customerHref}#CHANGE_EMAIL_ADDRESS`,
                  "Mobile phone": `${customerHref}#CHANGE_ADDRESS`,
                  Birthday: `${customerHref}#CHANGE_ADDRESS`,
                  "Delivery address": `${customerHref}#CHANGE_ADDRESS`,
                  "Billing address": `${customerHref}#CHANGE_ADDRESS`,
                  Company: `${customerHref}#CHANGE_BUSINESS_DETAILS`,
                  "VAT number": `${customerHref}#CHANGE_BUSINESS_DETAILS`,
                }
              : undefined
          }
        />
      </CollapsibleContent>
    </Collapsible>
  );
};

export default User;
